// src/components/Admin/Settings/Integration_Setting/IntegrationModalForm.jsx
import React, { useState, useMemo } from "react";
import { X, Save, Smartphone } from "lucide-react";

const PROVIDER_OPTIONS = [
  { key: "META", label: "Meta" },
  { key: "ENOTIFY", label: "Enotify" },
];

function getInitialProviderKey(item) {
  if (!item?.provider) return "";
  const p = String(item.provider).toUpperCase();
  if (p.includes("META")) return "META";
  if (p.includes("ENOTIFY")) return "ENOTIFY";
  return "";
}

export default function IntegrationFormModal({ item, onSave, onClose }) {
  const [formData, setFormData] = useState({
    providerKey: getInitialProviderKey(item),
    instanceId: item?.instanceId || "",
    templateName: item?.templateName || "",
    accountId: item?.accountId || "",
    authKey: item?.authKey || "",
    apiUrl: item?.apiUrl || "",
  });

  const isEnotify = formData.providerKey === "ENOTIFY";
  const isMeta = formData.providerKey === "META";

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  // validation for button enable/disable
  const isValid = useMemo(() => {
    if (!formData.providerKey) return false;

    if (isEnotify) {
      return formData.instanceId.trim().length > 0;
    }

    if (isMeta) {
      return (
        formData.templateName.trim() &&
        formData.accountId.trim() &&
        formData.authKey.trim() &&
        formData.apiUrl.trim()
      );
    }

    return false;
  }, [formData, isEnotify, isMeta]);

  const handleSubmit = (e) => {
    e.preventDefault();

    const isEnotifyLocal = formData.providerKey === "ENOTIFY";

    const providerLabel =
      formData.providerKey === "META" ? "Meta" : "Enotify";

    // payload similar to your Angular formattedResult
    const payload = {
      notificationType: "WHATSAPP",
      provider: providerLabel,
      instanceId: isEnotifyLocal ? formData.instanceId : "",
      templateName: isEnotifyLocal ? null : formData.templateName,
      accountId: isEnotifyLocal ? null : formData.accountId,
      authKey: isEnotifyLocal ? null : formData.authKey,
      apiUrl: isEnotifyLocal ? null : formData.apiUrl,
    };

    onSave(payload);
  };

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl overflow-hidden">
        {/* Header - Teal themed */}
        <div className="bg-gradient-to-r from-teal-600 to-teal-700 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-white/20 rounded-lg">
              <Smartphone className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-white">
                {item ? "Edit WhatsApp Provider" : "New WhatsApp Provider"}
              </h2>
              <p className="text-xs text-teal-100">
                Configure provider settings
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/20 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-white" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit}>
          <div className="px-6 py-5">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Select Provider */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Select Provider<span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.providerKey}
                  onChange={(e) => handleChange("providerKey", e.target.value)}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                >
                  <option value="">Select Provider</option>
                  {PROVIDER_OPTIONS.map((opt) => (
                    <option key={opt.key} value={opt.key}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Instance ID – only for Enotify */}
              {isEnotify && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Instance ID<span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.instanceId}
                    onChange={(e) => handleChange("instanceId", e.target.value)}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    placeholder="Enter Instance ID"
                  />
                </div>
              )}

              {/* Meta fields */}
              {isMeta && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      Template Name<span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.templateName}
                      onChange={(e) => handleChange("templateName", e.target.value)}
                      className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                      placeholder="Enter Template Name"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      Account ID<span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.accountId}
                      onChange={(e) => handleChange("accountId", e.target.value)}
                      className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                      placeholder="Enter Account ID"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      Auth Token<span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.authKey}
                      onChange={(e) => handleChange("authKey", e.target.value)}
                      className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                      placeholder="Enter Auth Token"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      Api Url<span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.apiUrl}
                      onChange={(e) => handleChange("apiUrl", e.target.value)}
                      className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                      placeholder="Enter Api Url"
                    />
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="bg-gray-50 border-t border-gray-200 px-6 py-4 flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-5 py-2.5 bg-white border border-gray-200 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 hover:border-gray-300 transition-all"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!isValid}
              className={`px-5 py-2.5 rounded-lg text-sm font-medium text-white flex items-center gap-2 transition-all ${
                isValid
                  ? "bg-teal-600 hover:bg-teal-700"
                  : "bg-gray-300 cursor-not-allowed"
              }`}
            >
              <Save className="w-4 h-4" />
              {item ? "Update" : "Add"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
